import type { OrganizationUnitType } from '../models/organization.model';

/**
 * Which organization units carry configuration of their own.
 *
 * Customer decision of 2026-09-22: configuration is maintained on the root and
 * on companies, no longer on departments. A department inherits what the
 * nearest company or the root defines — which it already did whenever it held
 * nothing itself; the change is that holding something itself is no longer an
 * option.
 *
 * Configuration here means the master data an OE owns: cost centres, booking
 * types, time types, day programs, Zeitmodelle, accounts, rule sets. It does
 * **not** mean the unit's own record — a department still has a name, a
 * timezone, a core time and a place in the tree, and all of those stay
 * editable on it.
 */
const CONFIGURABLE_TYPES: readonly OrganizationUnitType[] = [
  'organization',
  'company',
  // Added 2026-10-21 with the profit centre, whose whole point is that it is
  // configured like the company above it and differs only in what may hang
  // beneath it. A department is deliberately absent.
  'profit_center',
];

export function canHoldConfiguration(type: OrganizationUnitType): boolean {
  return CONFIGURABLE_TYPES.includes(type);
}

/**
 * Which unit keeps the holiday calendars.
 *
 * The root alone, by the customer's decision of 2026-10-26: the calendars
 * are kept once, per country, for the countries the organigram contains, so
 * a second set on a subsidiary would be a second truth about the same days.
 *
 * Its own predicate rather than a comparison spelled out at each call site —
 * there are three, and the machinery underneath is deliberately still
 * subtree-aware so that opening this up to subsidiaries later is a change
 * here and nowhere else.
 */
export function canHoldPublicHolidays(type: OrganizationUnitType): boolean {
  return type === 'organization';
}

/**
 * The settings a unit keeps on its own record, as opposed to beside it.
 *
 * Country and timezone are configuration too — the form's own hints say as
 * much ("leave empty to take it from the unit above"). A department inherits
 * both, so the fields are not shown for one.
 *
 * The Kernzeit was on this list until 2026-09-25. It now hangs on the weekday
 * of a Zeitmodell, which is what actually describes a working day.
 *
 * The two holiday parameters joined on 2026-09-28. They are inherited scalars
 * of the same kind, so putting them here is what makes a department refuse
 * them without a second rule being written for it.
 */
export const OE_OWN_SETTINGS = [
  'countryCode',
  'timezone',
  'vacationWaitingPeriodMonths',
  'vacationYearType',
] as const;

export type OeOwnSetting = (typeof OE_OWN_SETTINGS)[number];

type SettingValues = Partial<
  Record<OeOwnSetting, string | number | null | undefined>
>;

/**
 * The settings this change would newly put on a unit that keeps none.
 *
 * Empty for a unit that does keep them, and empty for a change that only
 * clears — clearing is how a row from before the rule is tidied up.
 *
 * Only a setting that is **new** counts. A value already on the row may be
 * sent back unchanged: the form submits every field on every save, so judging
 * the value rather than its arrival would make a department that predates the
 * rule unsavable, down to correcting its name.
 */
export function settingsNotKept(
  type: OrganizationUnitType,
  next: SettingValues,
  current: SettingValues = {},
): OeOwnSetting[] {
  if (canHoldConfiguration(type)) return [];

  // `''` is how the form sends a cleared text field. Written out rather than
  // `!value`, because a waiting period of `0` is a decision somebody made —
  // "entitled immediately" — and must not be read as "left empty".
  const isEmpty = (value: string | number | null | undefined) =>
    value === undefined || value === null || value === '';

  return OE_OWN_SETTINGS.filter(
    (key) => !isEmpty(next[key]) && isEmpty(current[key]),
  );
}
